import React, { useRef } from 'react';

import { Product } from 'src/utils/helpers/product';
import { useIntersectionObserver } from 'src/hooks/use-intersection-observer';

import ShoppingBags from 'src/assets/shopping-bags-icon';

import { BuyItAgainListItem } from '../buy-it-again-list/buy-it-again-list-item';
import {
  GradientEnd,
  GradientStart,
  ListPlaceholder,
  ProductList,
} from '../buy-it-again-list/buy-it-again-list.styles';

type ItemsTabProps = {
  backgroundColor?: string;
  products: Product[];
  handleProductClick: (product: Product, index: number) => void;
  handleQuickAddToCart: (product: Product, index: number) => void;
};

const listPadding = 20;

/** Renders the scrollable product list with gradient edges for the "Items" tab. */
export function ItemsTab({
  backgroundColor,
  products,
  handleProductClick,
  handleQuickAddToCart,
}: ItemsTabProps): JSX.Element {
  const startWatcherRef = useRef<HTMLDivElement>(null);
  const startWatcher = useIntersectionObserver(startWatcherRef);
  const endWatcherRef = useRef<HTMLDivElement>(null);
  const endWatcher = useIntersectionObserver(endWatcherRef);

  const showPlaceholder = products.length < 2;

  return (
    <div role='tabpanel' id='items-tab-panel'>
      <GradientStart isVisible={!showPlaceholder && !startWatcher?.isIntersecting} data-testid='list-gradient-start' />
      <ProductList padding={listPadding} showPlaceholder={showPlaceholder}>
        <div ref={startWatcherRef} />
        {products.map((product, index) => (
          <BuyItAgainListItem
            key={product.id}
            product={product}
            productIndex={index}
            onClick={() => handleProductClick(product, index)}
            onQuickAddClick={() => handleQuickAddToCart(product, index)}
          />
        ))}
        {showPlaceholder && (
          <ListPlaceholder backgroundColor={backgroundColor} data-testid='buy-it-again-list-placeholder'>
            <ShoppingBags />
            <p>
              Your recent purchases will show up here.{'\n'}
              Easily buy your favorites again and again!
            </p>
          </ListPlaceholder>
        )}
        <div ref={endWatcherRef} />
      </ProductList>
      <GradientEnd isVisible={!showPlaceholder && !endWatcher?.isIntersecting} data-testid='list-gradient-end' />
    </div>
  );
}
